import { Box } from '@chakra-ui/react';
import * as math from 'mathjs';
import { useCallback, useMemo, useState } from 'react';
import {
  LineSegment,
  LineSegment2,
  Point,
} from '../../../domain/geometry/geometric-types';
import { projectInDOM, useCameraChangedEvent } from './util';
import { EditableLengthLabel } from './EditableLengthLabel';

type DragFeedbackLineProps = {
  origin: Point;
  current: Point;
  viewer: Autodesk.Viewing.Viewer3D;
  isEditable?: boolean;
  isDragging?: boolean;
  onLengthSubmit?: (newLengthInMeters: number) => void;
};

/**
 * Renders a faint dashed line from the drag origin to the current position,
 * with a length label at the midpoint. Used to show how far the user has dragged
 * a vertex or edge.
 */
export function DragFeedbackLine({
  origin,
  current,
  viewer,
  isEditable = false,
  isDragging = false,
  onLengthSubmit,
}: DragFeedbackLineProps) {
  const [cameraPosition, setCameraPosition] = useState({});

  const positions2D = useMemo<LineSegment2>(
    () => [projectInDOM(origin, viewer), projectInDOM(current, viewer)],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cameraPosition, origin, current, viewer]
  );

  useCameraChangedEvent(
    viewer,
    useCallback(() => {
      setCameraPosition({});
    }, [])
  );

  const length = math.distance(positions2D[0], positions2D[1]) as number;

  const [x1, y1] = positions2D[0];
  const [x2, y2] = positions2D[1];
  const angle = (Math.atan2(y2 - y1, x2 - x1) * 180) / Math.PI;

  const segment: LineSegment = [origin, current];

  // Always show the label, but only show the line if it's long enough
  const showLine = length >= 3;

  // Use gray while dragging, blue when editable (after drag)
  const lineColor = isDragging
    ? 'var(--chakra-colors-gray-500)'
    : 'var(--chakra-colors-blue-500)';

  return (
    <>
      {/* Faint dashed line from origin to current position */}
      {showLine && (
        <Box
          position="absolute"
          zIndex={10}
          width="2px"
          pointerEvents="none"
          style={{
            top: y1,
            left: x1,
            height: `${length}px`,
            transformOrigin: '1px 0',
            transform: `rotate(${angle - 90}deg)`,
            backgroundImage: `repeating-linear-gradient(0deg, transparent, transparent 6px, ${lineColor} 6px, ${lineColor} 12px)`,
          }}
        />
      )}
      {/* Length label at the midpoint */}
      <EditableLengthLabel
        segment={segment}
        viewer={viewer}
        isEditable={isEditable}
        isDragging={isDragging}
        onLengthSubmit={onLengthSubmit}
      />
    </>
  );
}
